import { Link } from "react-router-dom";
import { useState } from "react";
import { useSeo } from "@/hooks/useSeo";
import { faqItems } from "@/mocks/faq";

const formUrl = "https://readdy.ai/api/form/da1dt8lrlbpoffc5h4tg";

export default function Faq() {
  const groups = ["阅读与追更", "分类与内容", "账号与设备", "更新与建议"];
  const [open, setOpen] = useState<Record<string, boolean>>({ [faqItems[0].q]: true });
  const [status, setStatus] = useState<string>("");
  const [formError, setFormError] = useState<string>("");

  useSeo({
    title: "常见问题_汗汗漫画悬疑推理追更帮助中心",
    description:
      "汗汗漫画常见问题汇总，涵盖追更方法、分类区别、阅读设备、更新频率等疑问。解决你在阅读悬疑推理漫画过程中遇到的各种问题。",
    keywords: "汗汗漫画,汗汗漫画常见问题,悬疑推理漫画怎么追更",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqItems.map((item) => ({
          "@type": "Question",
          name: item.q,
          acceptedAnswer: {
            "@type": "Answer",
            text: item.a,
          },
        })),
      },
    ],
  });

  const toggle = (q: string) =>
    setOpen((prev) => ({ ...prev, [q]: !prev[q] }));

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError("");
    const form = e.currentTarget;
    const fd = new FormData(form);
    // 蜜罐检测
    const honeypot = (fd.get("company_alt") as string) || "";
    if (honeypot.trim()) {
      setStatus("已收到你的反馈，我们会尽快处理。");
      form.reset();
      return;
    }
    fd.delete("company_alt");

    const action = form.getAttribute("action");
    if (!action) return;
    const body = new URLSearchParams();
    fd.forEach((value, key) => {
      const v = value as string;
      if (v && v.trim()) body.append(key, v.trim());
    });

    try {
      const res = await fetch(action, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; message?: string } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.message ||
        parsed?.message ||
        "";
      if (
        !res.ok ||
        (parsed && parsed.code && parsed.code !== "OK") ||
        /spam|form data is spam/i.test(responseText + serverMsg)
      ) {
        setFormError(serverMsg || "提交失败，请稍后再试。");
        return;
      }
      setStatus("感谢反馈！我们会认真阅读并尽快回复。");
      form.reset();
    } catch {
      setFormError("网络异常，请稍后再试。");
    }
  };

  return (
    <div className="mx-auto w-full max-w-[1100px] px-4 py-10 md:px-6 md:py-16">
      <nav aria-label="面包屑" className="flex flex-wrap items-center gap-1 text-sm text-foreground-500">
        <Link to="/" className="cursor-pointer transition-colors hover:text-accent-300">首页</Link>
        <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
        <span className="font-medium text-accent-300">常见问题</span>
      </nav>

      <header className="mt-6 text-center">
        <span className="inline-flex items-center gap-1 rounded-full border border-accent-300/40 bg-accent-500/10 px-4 py-1.5 text-xs font-medium text-accent-200">
          <i className="ri-question-answer-line" aria-hidden="true"></i>
          帮助中心
        </span>
        <h1 className="mt-4 font-heading text-3xl font-black text-foreground-50 md:text-4xl">
          常见问题
        </h1>
        <p className="mx-auto mt-3 max-w-xl text-sm text-foreground-500 md:text-base">
          关于追更、阅读与分类的常见疑问，这里都有答案。若仍未解决，欢迎在下方留言反馈。
        </p>
      </header>

      <div className="mt-10 space-y-10">
        {groups.map((group) => {
          const items = faqItems.filter((i) => i.group === group);
          if (!items.length) return null;
          return (
            <section key={group}>
              <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-50">
                <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary-500/15 text-primary-400">
                  <i className="ri-focus-3-line" aria-hidden="true"></i>
                </span>
                {group}
              </h2>
              <div className="mt-4 space-y-3">
                {items.map((item) => (
                  <div
                    key={item.q}
                    className="overflow-hidden rounded-lg border border-background-200/60 bg-background-100"
                  >
                    <button
                      type="button"
                      onClick={() => toggle(item.q)}
                      className="flex w-full cursor-pointer items-center justify-between gap-4 px-4 py-4 text-left md:px-5"
                    >
                      <span className="text-sm font-semibold text-foreground-100 md:text-base">
                        {item.q}
                      </span>
                      <i
                        className={`ri-add-line text-lg text-accent-300 transition-transform ${
                          open[item.q] ? "rotate-45" : ""
                        }`}
                        aria-hidden="true"
                      ></i>
                    </button>
                    {open[item.q] && (
                      <p className="border-t border-background-200/50 px-4 py-4 text-sm leading-relaxed text-foreground-500 md:px-5">
                        {item.a}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </section>
          );
        })}
      </div>

      {/* 意见反馈表单 */}
      <section className="mt-14 rounded-lg border border-background-200/60 bg-background-100 p-6 md:p-10">
        <div className="flex flex-col gap-2 text-center">
          <h2 className="font-heading text-2xl font-bold text-foreground-50 md:text-3xl">
            还有疑问或想推荐作品？
          </h2>
          <p className="text-sm text-foreground-500">
            填写下方表单，我们会认真阅读每一条反馈
          </p>
        </div>

        <form
          data-readdy-form="faq-feedback"
          className="mx-auto mt-8 max-w-xl space-y-4"
          action={formUrl}
          onSubmit={handleSubmit}
        >
          <input
            type="text"
            name="company_alt"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            readOnly
            className="hp-field"
          />
          <div>
            <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-foreground-300">
              你的邮箱
            </label>
            <input
              id="email"
              name="email"
              type="email"
              required
              className="w-full rounded-md border border-background-300/60 bg-background-900/40 px-4 py-3 text-sm text-foreground-100 outline-none transition-colors focus:border-accent-300/60"
              placeholder="用于接收回复"
            />
          </div>
          <div>
            <label htmlFor="subject" className="mb-1.5 block text-sm font-medium text-foreground-300">
              反馈主题
            </label>
            <select
              id="subject"
              name="subject"
              required
              className="w-full cursor-pointer rounded-md border border-background-300/60 bg-background-900/40 px-4 py-3 text-sm text-foreground-100 outline-none transition-colors focus:border-accent-300/60"
            >
              <option value="推荐作品">推荐作品</option>
              <option value="问题咨询">问题咨询</option>
              <option value="功能建议">功能建议</option>
              <option value="其他">其他</option>
            </select>
          </div>
          <div>
            <label htmlFor="message" className="mb-1.5 block text-sm font-medium text-foreground-300">
              留言内容
            </label>
            <textarea
              id="message"
              name="message"
              required
              maxLength={500}
              rows={4}
              className="w-full resize-none rounded-md border border-background-300/60 bg-background-900/40 px-4 py-3 text-sm text-foreground-100 outline-none transition-colors focus:border-accent-300/60"
              placeholder="告诉我们你的想法（500字以内）"
            ></textarea>
          </div>

          {status && (
            <p className="rounded-md border border-accent-300/40 bg-accent-500/10 px-4 py-3 text-sm text-accent-200">
              {status}
            </p>
          )}
          {formError && (
            <p className="rounded-md border border-primary-400/40 bg-primary-500/10 px-4 py-3 text-sm text-primary-300">
              {formError}
            </p>
          )}

          <button
            type="submit"
            className="w-full cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            提交反馈
          </button>
        </form>
      </section>
    </div>
  );
}